/**
 * Reduces the `tool_call` / `tool_result` / `tool_steps_discarded` SSE events
 * into the ordered `ToolStep[]` list a chat turn accumulates while streaming.
 *
 * Extracted from `useConversationChat.ts` (same rationale as
 * `sseFrameParser.ts`: a pure function is unit-testable without the
 * fetch/ReadableStream plumbing around it).
 *
 * Ordering contract: a `tool_call` appends a new step in `status: "running"`;
 * the matching `tool_result` (same `call_id`) resolves that step in place.
 * `tool_steps_discarded` marks every accumulated step for the turn as
 * discarded -- they stay in the list (visually marked in the UI), never
 * removed, so a gated/errored turn does not silently drop tool activity the
 * user already saw stream in.
 *
 * Returns the same array reference when an event does not affect tool
 * steps, so callers can skip a re-render by comparing references.
 */

import type { ToolStep } from '../../api/data-contracts';
import type { SseEvent } from './sseFrameParser';

export function applyToolStepEvent(steps: ToolStep[], event: SseEvent): ToolStep[] {
  switch (event.type) {
    case 'tool_call':
      return [
        ...steps,
        {
          call_id: event.call_id,
          server_key: event.server_key,
          tool_name: event.tool_name,
          arguments: event.arguments,
          status: 'running',
        },
      ];

    case 'tool_result': {
      const index = steps.findIndex((step) => step.call_id === event.call_id);
      if (index === -1) {
        // Defensive: a tool_result without a preceding tool_call for this
        // call_id should not happen (S11 pins the tool_call -> tool_result
        // ordering), but a dropped or reordered frame must not crash the UI
        // -- surface what we do know rather than silently discarding it.
        return [
          ...steps,
          {
            call_id: event.call_id,
            server_key: event.server_key,
            tool_name: event.tool_name,
            arguments: '',
            status: event.status,
            summary: event.summary,
          },
        ];
      }

      const next = [...steps];
      next[index] = { ...next[index], status: event.status, summary: event.summary };
      return next;
    }

    case 'tool_steps_discarded':
      if (steps.length === 0) {
        return steps;
      }
      return steps.map((step) => ({ ...step, discarded: true }));

    default:
      return steps;
  }
}
